import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { money } from "@/components/am/shared";

export function PlanPicker({ v, setV }) {
  const [busy, setBusy] = useState(false);
  if (v.quote.locked || !v.plan_options?.length) return null;
  const rec = v.plan_recommendation;
  const pick = async (plan) => { setBusy(true); try { setV((await api.put(`/diy-sites/${v.project_id}/plan`, { plan })).data); toast.success("Starting plan changed"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return (
    <div className="rounded-xl border border-slate-800 p-4 space-y-2" data-testid="diy-plan-picker">
      <div className="text-sm font-semibold">Starting plan (for your current selection)</div>
      <div className="grid sm:grid-cols-3 gap-2">{v.plan_options.map((o) => (
        <button key={o.plan} disabled={busy || o.plan === v.plan_key} onClick={() => pick(o.plan)} data-testid={`diy-plan-${o.plan}`}
          className={`text-left rounded-lg border p-2 text-sm ${o.plan === v.plan_key ? "border-emerald-500 bg-emerald-500/5" : "border-slate-800 hover:border-slate-600"}`}>
          <div className="font-medium">{o.label}</div><div className="font-mono text-xs">{money(o.total)}{o.plan === v.plan_key ? " · current" : ""}</div>
        </button>))}</div>
      {rec && <div className="rounded-lg bg-emerald-500/10 p-3 text-sm flex flex-wrap items-center justify-between gap-2" data-testid="diy-plan-recommendation">
        <span>With your current pages and features, <b>{rec.label}</b> costs <b className="font-mono">{money(rec.total)}</b> instead of <b className="font-mono">{money(v.quote.total)}</b> on your current plan.</span>
        <Button size="sm" disabled={busy} onClick={() => pick(rec.plan)} data-testid="diy-switch-plan">Switch and save {money(v.quote.total - rec.total)}</Button></div>}
    </div>
  );
}

export function ChangeRequest({ v, setV }) {
  const [ok, setOk] = useState(false); const [busy, setBusy] = useState(false);
  const cr = v.change_request;
  if (!cr) return null;
  const run = async (fn) => { setBusy(true); try { setV((await fn()).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const R = ({ k, val, t }) => <div className="flex justify-between text-sm"><span>{k}</span><span className="font-mono" data-testid={t}>{val}</span></div>;
  return (
    <div className="rounded-xl border border-amber-500/50 p-4 space-y-2" data-testid="diy-change-request">
      <div className="text-sm font-semibold">Revised quote waiting for your approval</div>
      <R k="Current approved price" val={money(cr.old_total)} t="diy-cr-old" />
      <R k="Revised price" val={money(cr.new_total)} t="diy-cr-new" />
      <R k={cr.difference >= 0 ? "Increase" : "Decrease"} val={money(Math.abs(cr.difference))} t="diy-cr-diff" />
      <R k="Already paid" val={money(cr.paid)} />
      <R k="New outstanding balance" val={money(cr.new_balance)} t="diy-cr-balance" />
      {cr.refund_due > 0 && <R k="Refund due (approved manually by LaunchX360)" val={money(cr.refund_due)} t="diy-cr-refund" />}
      {cr.auto_added?.length > 0 && <p className="text-[11px] text-slate-500">Also added because they are needed: {cr.auto_added.join(", ")}</p>}
      <p className="text-[11px] text-slate-500">Nothing is charged or refunded now. Approving updates your pages, features, price and balance. Any increase is paid with the balance before publishing.</p>
      <label className="flex gap-2 text-sm"><Checkbox checked={ok} onCheckedChange={(x) => setOk(!!x)} data-testid="diy-cr-confirm" />I approve the revised price of {money(cr.new_total)}.</label>
      <div className="flex gap-2">
        <Button disabled={!ok || busy} onClick={() => run(() => api.post(`/diy-sites/${v.project_id}/change-request/approve`, { confirm: true }))} data-testid="diy-cr-approve">Approve change</Button>
        <Button variant="secondary" disabled={busy} onClick={() => run(() => api.delete(`/diy-sites/${v.project_id}/change-request`))} data-testid="diy-cr-cancel">Cancel</Button>
      </div>
    </div>
  );
}
